import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import Input from "@components/Input";
import Button from "@components/Button";
import Checkbox from "@components/Checkbox";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import { toast } from "react-toastify";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import { Farm } from "@services/farmsService";
import {
  AccessGroup,
  ClientAccess,
  GroupInput,
  createGroup,
  updateGroup,
  deleteGroup,
} from "@services/teamService";
import { DRIVER_TEMPLATE } from "./roles";

// "Calibrate machines" (phase 2 of the driver-access spec, §7): lets the driver
// app upload calibration runs for the group's machines. Web calibration CRUD
// still needs Can edit / Can delete. Kept as a flag so the switch can be hidden
// again without touching the load/save path, which always carries the value.
const SHOW_CALIBRATE = true;

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSaved: () => void;
  farms: Farm[];
  group?: AccessGroup | null; // editing when set
}

const CapRow: React.FC<{
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, hint, checked, onChange }) => (
  <Row alignItems="center" justifyContent="space-between" style={{ padding: "6px 0" }}>
    <Column>
      <Text size={14} type="heading" color="#494C5B">
        {label}
      </Text>
      {hint && (
        <Text size={12} color="#899093">
          {hint}
        </Text>
      )}
    </Column>
    <Checkbox checked={checked} onChange={onChange} />
  </Row>
);

const RadioDot = styled.div<{ active: boolean }>`
  width: 16px;
  height: 16px;
  min-width: 16px;
  border-radius: 50%;
  box-sizing: border-box;
  border: ${({ active }) => (active ? "5px solid #68ad18" : "1px solid #c3c8ca")};
  background: #ffffff;
`;

const ChoiceRow: React.FC<{
  label: string;
  hint?: string;
  active: boolean;
  onSelect: () => void;
}> = ({ label, hint, active, onSelect }) => (
  <Row
    alignItems="center"
    justifyContent="space-between"
    style={{ padding: "6px 0", cursor: "pointer" }}
    onClick={onSelect}
    role="radio"
    aria-checked={active}
  >
    <Column>
      <Text size={14} type="heading" color="#494C5B">
        {label}
      </Text>
      {hint && (
        <Text size={12} color="#899093">
          {hint}
        </Text>
      )}
    </Column>
    <RadioDot active={active} />
  </Row>
);

const GroupDialog: React.FC<IProps> = ({ visible, onClose, onSaved, farms, group }) => {
  const lang = useLang();
  const t = (lang as any).team ?? {};
  const editing = !!group;

  const [name, setName] = useState("");
  const [allFarms, setAllFarms] = useState(false);
  const [farmIds, setFarmIds] = useState<string[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canExport, setCanExport] = useState(false);
  const [canAccessDevices, setCanAccessDevices] = useState(false);
  const [canViewFields, setCanViewFields] = useState(true);
  const [canRecordLoads, setCanRecordLoads] = useState(false);
  const [canCalibrate, setCanCalibrate] = useState(false);
  const [clientAccess, setClientAccess] = useState<ClientAccess>("all");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setName(group?.name ?? "");
    setAllFarms(group?.allFarms ?? false);
    setFarmIds(group?.farmIds ?? []);
    setCanEdit(group?.canEdit ?? false);
    setCanDelete(group?.canDelete ?? false);
    setCanExport(group?.canExport ?? false);
    setCanAccessDevices(group?.canAccessDevices ?? false);
    setCanViewFields(group?.canViewFields ?? true);
    setCanRecordLoads(group?.canRecordLoads ?? false);
    setCanCalibrate(group?.canCalibrate ?? false);
    setClientAccess(group?.clientAccess ?? "all");
  }, [visible, group]);

  // Pre-ticks the usual driver switches. Name and farms are the admin's: the
  // name is only suggested when empty, and farms are kept as selected.
  const applyDriverTemplate = () => {
    setCanRecordLoads(DRIVER_TEMPLATE.canRecordLoads);
    setCanCalibrate(DRIVER_TEMPLATE.canCalibrate);
    setCanViewFields(DRIVER_TEMPLATE.canViewFields);
    setCanAccessDevices(DRIVER_TEMPLATE.canAccessDevices);
    setCanEdit(DRIVER_TEMPLATE.canEdit);
    setCanDelete(DRIVER_TEMPLATE.canDelete);
    setCanExport(DRIVER_TEMPLATE.canExport);
    setClientAccess(DRIVER_TEMPLATE.clientAccess);
    if (!name.trim()) setName(t.driverTemplateName ?? "Drivers");
    if (!allFarms && farmIds.length === 0) {
      toast(
        t.driverTemplatePickFarms ??
          "Driver template applied. Now pick the farms whose machines this driver uses.",
        { type: "info" }
      );
    }
  };

  const toggleFarm = (id: string) =>
    setFarmIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleSave = async () => {
    if (!name.trim()) {
      toast(t.nameRequired ?? "Enter a group name", { type: "error" });
      return;
    }
    if (!allFarms && farmIds.length === 0) {
      toast(t.pickFarms ?? "Pick at least one farm (or All farms)", { type: "error" });
      return;
    }
    const payload: GroupInput = {
      name: name.trim(),
      allFarms,
      farmIds: allFarms ? [] : farmIds,
      canEdit,
      canDelete,
      canExport,
      canAccessDevices,
      canViewFields,
      canRecordLoads,
      canCalibrate,
      clientAccess,
    };
    setSaving(true);
    try {
      if (editing && group) await updateGroup(group.id, payload);
      else await createGroup(payload);
      toast(t.groupSaved ?? "Group saved", { type: "success" });
      onSaved();
      onClose();
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!group) return;
    if (!window.confirm(t.deleteGroupConfirm ?? "Delete this group?")) return;
    try {
      await deleteGroup(group.id);
      toast(t.groupDeleted ?? "Group deleted", { type: "success" });
      onSaved();
      onClose();
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    }
  };

  return (
    <Dialog
      style={{ width: 460 }}
      title={editing ? t.editGroup ?? "Edit group" : t.newGroup ?? "New group"}
      visible={visible}
      onClose={onClose}
    >
      <Column style={{ maxHeight: "70vh", overflow: "auto", paddingRight: 4 }}>
        <Input
          label={t.groupName ?? "Group name"}
          placeholder={t.groupNamePlaceholder ?? "e.g. Farm 1 managers"}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <SizedBox height={16} />

        <Text size={14} type="heading" color="#494C5B">
          {t.farmsAccess ?? "Farms this group can access"}
        </Text>
        <SizedBox height={8} />
        <CapRow
          label={t.allFarms ?? "All farms (including future ones)"}
          checked={allFarms}
          onChange={setAllFarms}
        />
        {!allFarms && (
          // flexShrink: 0 is load-bearing. The parent is a height-capped flex
          // column, and a scroll container has min-height 0, so without it this
          // list absorbs all the overflow and collapses to its padding.
          <Column
            crossAxisSize="max"
            style={{
              flexShrink: 0,
              maxHeight: 180,
              overflow: "auto",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              padding: "4px 12px",
            }}
          >
            {farms.length === 0 && (
              <Text size={13} color="#899093" style={{ padding: "8px 0" }}>
                {t.noFarms ?? "No farms yet"}
              </Text>
            )}
            {farms.map((f) => (
              <Row
                key={f.id}
                alignItems="center"
                justifyContent="space-between"
                style={{ padding: "6px 0" }}
              >
                <Text size={14} color="#494C5B">
                  {f.name}
                </Text>
                <Checkbox
                  checked={farmIds.includes(f.id)}
                  onChange={() => toggleFarm(f.id)}
                />
              </Row>
            ))}
          </Column>
        )}

        <SizedBox height={16} />
        <Row alignItems="center" justifyContent="space-between">
          <Text size={14} type="heading" color="#494C5B" fitContent nowrap>
            {t.permissions ?? "Permissions"}
          </Text>
          <Button
            kind="outline"
            onClick={applyDriverTemplate}
            title={
              t.driverTemplateHint ??
              "Record loads and calibrate from the driver app on the selected farms' machines, nothing else"
            }
            style={{ width: "fit-content", height: 30, fontSize: 13, padding: "0 12px" }}
          >
            {t.driverTemplate ?? "Driver template"}
          </Button>
        </Row>
        <SizedBox height={4} />
        <CapRow
          label={t.capViewFields ?? "View fields & yield maps"}
          hint={t.capViewFieldsHint ?? "Fields, boundaries, yield maps and field analytics"}
          checked={canViewFields}
          onChange={setCanViewFields}
        />
        <CapRow
          label={t.capRecordLoads ?? "Record loads"}
          hint={t.capRecordLoadsHint ?? "Save weighed loads for machines on these farms"}
          checked={canRecordLoads}
          onChange={setCanRecordLoads}
        />
        {SHOW_CALIBRATE && (
          <CapRow
            label={t.capCalibrate ?? "Calibrate machines"}
            hint={
              t.capCalibrateHint ??
              "Send calibrations from the driver app for machines on these farms"
            }
            checked={canCalibrate}
            onChange={setCanCalibrate}
          />
        )}
        <CapRow
          label={t.capDevices ?? "Access machines & all loads"}
          hint={t.capDevicesHint ?? "Devices, tracks, analytics and loads recorded by everyone"}
          checked={canAccessDevices}
          onChange={setCanAccessDevices}
        />
        <CapRow
          label={t.capEdit ?? "Can edit"}
          hint={t.capEditHint ?? "Create and modify fields, seasons, crops, maps"}
          checked={canEdit}
          onChange={setCanEdit}
        />
        <CapRow
          label={t.capDelete ?? "Can delete"}
          checked={canDelete}
          onChange={setCanDelete}
        />
        <CapRow
          label={t.capExport ?? "Can export"}
          hint={t.capExportHint ?? "Download files and send to John Deere"}
          checked={canExport}
          onChange={setCanExport}
        />

        <SizedBox height={16} />
        <Text size={14} type="heading" color="#494C5B">
          {t.clientAccessTitle ?? "Can use"}
        </Text>
        <SizedBox height={4} />
        <div role="radiogroup" style={{ width: "100%" }}>
          <ChoiceRow
            label={t.clientAll ?? "Web dashboard + driver app"}
            active={clientAccess === "all"}
            onSelect={() => setClientAccess("all")}
          />
          <ChoiceRow
            label={t.clientWeb ?? "Web dashboard only"}
            active={clientAccess === "web"}
            onSelect={() => setClientAccess("web")}
          />
          <ChoiceRow
            label={t.clientApp ?? "Driver app only"}
            hint={t.clientAppHint ?? "This account won't appear in their web dashboard"}
            active={clientAccess === "app"}
            onSelect={() => setClientAccess("app")}
          />
        </div>
      </Column>

      <SizedBox height={20} />
      <Row justifyContent="space-between" alignItems="center">
        {editing && group && group.memberCount === 0 ? (
          <Text
            size={14}
            color="#e5484d"
            fitContent
            nowrap
            style={{ cursor: "pointer" }}
            onClick={handleDelete}
          >
            {t.deleteGroup ?? "Delete group"}
          </Text>
        ) : (
          <div />
        )}
        <Row mainAxisSize="fit-content">
          <Button
            kind="outline"
            onClick={onClose}
            style={{ width: 120, marginRight: 12 }}
          >
            {t.cancel ?? "Cancel"}
          </Button>
          <Button onClick={handleSave} disabled={saving} style={{ width: 120 }}>
            {t.save ?? "Save"}
          </Button>
        </Row>
      </Row>
    </Dialog>
  );
};

export default observer(GroupDialog);
